import React, { useState, useRef, useEffect, useContext, createRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'
import Portal from './Portal'

//Internal imports
import { ScreenContext } from 'context/ScreenContext'
import { clearFormErrors } from 'modules/authentication/actions'
import { setChoiceButton } from 'modules/location/actions'

const stopPropagation = e => e.stopPropagation()
const callAll =
    (...fns) =>
    (...args) => {
        fns.forEach(fn => fn && fn(...args))
    }

export const useDialog = () => {
    const dispatch = useDispatch()
    const [on, setOn] = useState(false)
    const show = () => setOn(true)
    const hide = () => setOn(false)
    const toggle = () => {
        dispatch(clearFormErrors())
        setOn(!on)
        dispatch(setChoiceButton(null))
    }

    const getToggleProps = (props = {}) => ({
        'aria-controls': 'target',
        ...props,
        onClick: callAll(props.onClick, toggle),
    })

    const getContainerProps = (props = {}) => ({
        ...props,
        onClick: callAll(props.onClick),
        onKeyDown: callAll(props.onKeyDown, ({ keyCode }) => keyCode === 27 && hide()),
    })

    return {
        on,
        show,
        hide,
        toggle,
        getToggleProps,
        getContainerProps,
    }
}

export const useDialogProfileInfo = () => {
    const [onProfileInfo, setOnProfileInfo] = useState(false)
    const showProfileInfo = () => setOnProfileInfo(true)
    const hideProfileInfo = () => setOnProfileInfo(false)
    const toggleProfileInfo = () => setOnProfileInfo(!onProfileInfo)

    const getToggleProfileProps = (props = {}) => ({
        'aria-controls': 'target',
        ...props,
        onClick: callAll(props.onClick, toggleProfileInfo),
    })

    const getProfileContainerProps = (props = {}) => ({
        ...props,
        onKeyDown: callAll(props.onKeyDown, ({ keyCode }) => keyCode === 27 && hideProfileInfo()),
    })

    return {
        onProfileInfo,
        showProfileInfo,
        hideProfileInfo,
        toggleProfileInfo,
        getToggleProfileProps,
        getProfileContainerProps,
    }
}

export const useDialogSignInOptions = () => {
    const [showSignIn, setShowSignIn] = useState(false)
    const showSignInOptions = () => setShowSignIn(true)
    const hideSignInOptions = () => setShowSignIn(false)
    const toggle = () => setShowSignIn(!showSignIn)

    const getToggleSignInProps = (props = {}) => ({
        'aria-controls': 'target',
        'aria-expanded': Boolean(showSignIn),
        ...props,
        onClick: callAll(props.onClick, toggle),
    })

    const getSignInContainerProps = (props = {}) => ({
        ...props,
        onClick: callAll(props.onClick, toggle),
        onKeyDown: callAll(props.onKeyDown, ({ keyCode }) => keyCode === 27 && hideSignInOptions()),
    })

    return {
        showSignIn,
        showSignInOptions,
        hideSignInOptions,
        toggle,
        getToggleSignInProps,
        getSignInContainerProps,
    }
}

export const Container = props => {
    const { mobile } = useContext(ScreenContext)
    const isFrom7RewardsView = useSelector(state => state.appSettings.get('isApp'))
    const choiceButton = useSelector(state => state.location.getIn(['choiceButton']))
    const enteredAddress = useSelector(state => state.location.getIn(['enteredAddress']))
    const showClearCartMessage = useSelector(state =>
        state.location.getIn(['showClearCartMessage'])
    )
    const { className, placeOrderPopUp, ...restProps } = props
    const overlayEl = useRef(null)
    useEffect(() => {
        overlayEl.current.focus()
    })

    useEffect(() => {
        function keyListener(e) {
            const listener = keyListenersMap.get(e.keyCode)
            return listener && listener(e)
        }
        document.addEventListener('keydown', keyListener)
        return () => document.removeEventListener('keydown', keyListener)
    })

    const modalRef = createRef()
    const handleTabKey = e => {
        const fe = modalRef.current.querySelectorAll(
            'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
        )
        const focusableModalElements = []
        fe.forEach(f => {
            if (!f.disabled) {
                focusableModalElements.push(f)
            }
        })

        const firstElement = focusableModalElements[0]
        const lastElement = focusableModalElements[focusableModalElements.length - 1]

        if (!e.shiftKey && document.activeElement === lastElement) {
            firstElement.focus()
            return e.preventDefault()
        }

        if (e.shiftKey && document.activeElement === firstElement) {
            lastElement.focus()
            e.preventDefault()
        }
    }
    const keyListenersMap = new Map([[9, handleTabKey]])

    return (
        <Portal>
            <div
                className={classnames('dialog-overlay', className)}
                {...restProps}
                ref={overlayEl}
                aria-modal="true"
                tabIndex="-1"
                role="dialog"
            >
                <div
                    className={`dialog-content ${choiceButton === 'pickup' && enteredAddress == 2 && !showClearCartMessage ? 'dialog-content-pickup' : ''} ${
                        isFrom7RewardsView && mobile ? 'totalHeight' : null
                    } ${placeOrderPopUp ? 'placerOrderPopUp' :''}`}
                    onClick={stopPropagation}
                    ref={modalRef}
                >
                    {props.children}
                </div>
            </div>
        </Portal>
    )
}
